import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Please enter your email and password'); return; }
    setLoading(true);
    try {
      await login(email, password);
      const redir = params.get('redirect');
      navigate(redir && redir.startsWith('/') ? redir : '/');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center mb-8">
          <div className="w-14 h-14 rounded-full bg-green-600 flex items-center justify-center font-black text-black">PBB</div>
        </div>
        <h1 className="pbb-title-green text-3xl mb-2">Sign in</h1>
        <p className="text-sm text-gray-300 mb-8">
          Don't have an account?{' '}
          <Link to="/signup" className="pbb-link">Sign up Now</Link>
        </p>
        <form onSubmit={submit} className="space-y-4">
          <input
            className="pbb-input"
            type="email"
            placeholder="Enter email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <div className="relative">
            <input
              className="pbb-input pr-12"
              type={showPw ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            <button type="button" onClick={() => setShowPw(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <div className="flex items-center justify-between text-sm">
            <label className="inline-flex items-center gap-2 text-gray-300 cursor-pointer">
              <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)}
                className="accent-green-600" />
              Remember me
            </label>
            <Link to="/forgot-password" data-testid="forgot-password-link" className="underline text-blue-400 hover:text-blue-300">Forgot your password?</Link>
          </div>
          {error && <div className="text-red-400 text-sm">{error}</div>}
          <button type="submit" disabled={loading} className="pbb-btn-green disabled:opacity-70 inline-flex items-center justify-center gap-2">
            {loading && <Loader2 className="animate-spin" size={16}/>}
            {loading ? 'Signing in...' : 'Login'}
          </button>
        </form>
        <p className="text-center text-xs text-gray-500 mt-10">Promote Black Business</p>
      </div>
    </div>
  );
}
